import { useCallback, useMemo, useSyncExternalStore } from 'react';

import { useViewer } from '@/entities/viewer';

import { type CatalogFilters, DEFAULT_FILTERS, parseFilters } from './filters';

const listeners = new Set<() => void>();
const keyFor = (userId: string) => `zadok.catalogFilters.${userId}`;

function read(userId: string): string | null {
  try {
    return window.localStorage.getItem(keyFor(userId));
  } catch {
    return null;
  }
}

/**
 * The catalog filters of the signed-in person, remembered on this device (per user, no resource
 * data) and shared by every catalog on screen: changing one filter on the dashboard is the same
 * change in a workspace. A change to one field never touches the others.
 */
export function useCatalogFilters(): {
  filters: CatalogFilters;
  update: (patch: Partial<CatalogFilters>) => void;
  reset: () => void;
} {
  const viewer = useViewer();
  const userId = viewer.status === 'authenticated' ? viewer.user.id : null;
  const subscribe = useCallback((listener: () => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);
  const raw = useSyncExternalStore(subscribe, () => (userId ? read(userId) : null));
  const filters = useMemo(() => parseFilters(raw), [raw]);

  const write = useCallback(
    (next: CatalogFilters) => {
      if (!userId) return;
      try {
        window.localStorage.setItem(keyFor(userId), JSON.stringify(next));
      } catch {
        /* storage unavailable: the choice just is not remembered */
      }
      listeners.forEach((listener) => {
        listener();
      });
    },
    [userId],
  );
  const update = useCallback(
    (patch: Partial<CatalogFilters>) => {
      write({ ...parseFilters(userId ? read(userId) : null), ...patch });
    },
    [userId, write],
  );
  // Reset clears the filters, not the chosen order.
  const reset = useCallback(() => {
    write({ ...DEFAULT_FILTERS, sort: parseFilters(userId ? read(userId) : null).sort });
  }, [userId, write]);
  return { filters, update, reset };
}
